import { useContext, useMemo, useState } from "react";
import { CalendarPlus, Plus, RotateCcw } from "lucide-react";
import { MeetingsProvider, useMeetings, EMPTY_FILTERS } from "./modal/MeetingsContext";
import MeetingForm from "./ui/MeetingForm";
import MeetingRow from "./ui/MeetingRow";
import MeetingDetailModal from "./ui/MeetingDetailModal";
import EmployeePanel from "./ui/EmployeePanel";
import SummaryStrip from "./ui/SummaryStrip";
import OverdueChips, { matchesOverdue } from "./ui/OverdueChips";
import type { OverdueFilter } from "./ui/OverdueChips";
import { formatMeetingDate, formatDaysAgo, overdueTier, OVERDUE_DAYS, sortByNeed } from "./ui/meetingUtils";
import Alert from "shared/Alert";
import { CONDUCTOR_ROLES } from "./api/MeetingsApi";
import type { Meeting } from "./api/MeetingsApi";
import { VerifyContext } from "../../app/VerifyContext";
import ConfirmModal from "shared/ConfirmModal";
import PageHeader from "shared/PageHeader";
import Button from "shared/Button";
import FilterChip from "shared/FilterChip";
import SearchInput from "shared/SearchInput";
import PagerNav from "shared/PagerNav";
import { FORM_CONTROL } from "shared/tableStyles";
import { cn } from "lib/utils";

function MeetingsContent() {
  const {
    employees, employeesLoading, meetings, meetingsLoading, summary, error,
    totalCount, totalPages, page, filters,
    setFilters, setPage, canEdit,
    createMeeting, updateMeeting, deleteMeeting,
  } = useMeetings();
  const { superAdmin } = useContext(VerifyContext);

  const [searchText, setSearchText] = useState("");
  const [overdueFilter, setOverdueFilter] = useState<OverdueFilter>("");
  const [formEmployeeId, setFormEmployeeId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null);
  const [viewingMeeting, setViewingMeeting] = useState<Meeting | null>(null);
  const [deleting, setDeleting] = useState<Meeting | null>(null);

  const neverCount = useMemo(() => employees.filter((e) => overdueTier(e.last_meeting_date) === "never").length, [employees]);
  const staleCount = useMemo(() => employees.filter((e) => overdueTier(e.last_meeting_date) === "stale").length, [employees]);
  const mobileEmployees = useMemo(
    () => sortByNeed(employees).filter((e) => matchesOverdue(overdueFilter, e.last_meeting_date)),
    [employees, overdueFilter],
  );
  const selectedEmployee = useMemo(
    () => employees.find((e) => e.id === filters.employeeId) ?? null,
    [employees, filters.employeeId],
  );

  const viewingIndex = viewingMeeting ? meetings.findIndex((m) => m.id === viewingMeeting.id) : -1;

  // Filters other than the employee (the employee is chosen in the left panel).
  const hasActiveFilters =
    filters.search !== EMPTY_FILTERS.search ||
    filters.conductorRole !== EMPTY_FILTERS.conductorRole ||
    filters.dateFrom !== EMPTY_FILTERS.dateFrom ||
    filters.dateTo !== EMPTY_FILTERS.dateTo;

  function openAdd(employeeId: number | null = filters.employeeId) { setEditingMeeting(null); setFormEmployeeId(employeeId); setShowForm(true); }
  function openEdit(m: Meeting) { setViewingMeeting(null); setEditingMeeting(m); setFormEmployeeId(m.employee_id); setShowForm(true); }
  function clearFilters() {
    setSearchText("");
    setFilters({ search: "", conductorRole: "", dateFrom: "", dateTo: "" });
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Employee Meetings"
        description="1:1 meetings to raise problems or issues with an employee. Scored reviews live under Employee Evaluation."
        actions={canEdit && (
          <Button variant="primary" onClick={() => openAdd()}>
            <Plus className="w-4 h-4 mr-1.5 inline" />
            New meeting
          </Button>
        )}
      />

      {(neverCount > 0 || staleCount > 0) && (
        <Alert tone="warning" role="status" className="text-sm flex flex-wrap items-center gap-x-4 gap-y-1">
          {neverCount > 0 && (
            <span>
              <strong>{neverCount}</strong> {neverCount === 1 ? "employee has" : "employees have"} never had a meeting.{" "}
              {overdueFilter !== "never" && <button type="button" onClick={() => setOverdueFilter("never")} className="underline font-medium">Show</button>}
            </span>
          )}
          {staleCount > 0 && (
            <span>
              <strong>{staleCount}</strong> {staleCount === 1 ? "employee has" : "employees have"} had no meeting in over {OVERDUE_DAYS} days.{" "}
              {overdueFilter !== "stale" && <button type="button" onClick={() => setOverdueFilter("stale")} className="underline font-medium">Show</button>}
            </span>
          )}
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[18rem_minmax(0,1fr)] gap-5 items-start">
        {/* Mobile: a select; desktop: the searchable list. */}
        <div className="lg:hidden flex flex-col gap-2">
          <select
            className={cn(FORM_CONTROL, "w-full px-3 py-2.5")}
            aria-label="Employee"
            value={filters.employeeId ?? ""}
            onChange={(e) => setFilters({ employeeId: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">All employees</option>
            {mobileEmployees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name} · {e.last_meeting_date ? `last ${formatMeetingDate(e.last_meeting_date, false)} (${formatDaysAgo(e.last_meeting_date)})` : "no meetings"}
                {e.meeting_count > 0 ? ` · ${e.meeting_count}` : ""}
              </option>
            ))}
          </select>
          <OverdueChips employees={employees} value={overdueFilter} onChange={setOverdueFilter} />
        </div>
        <div className="hidden lg:block">
          <EmployeePanel
            employees={employees}
            loading={employeesLoading}
            selectedId={filters.employeeId}
            onSelect={(id) => setFilters({ employeeId: id })}
            overdueFilter={overdueFilter}
            onOverdueFilterChange={setOverdueFilter}
            onLogMeeting={canEdit ? (id) => openAdd(id) : undefined}
          />
        </div>

        <div className="flex flex-col gap-4 min-w-0">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {selectedEmployee ? selectedEmployee.name : "All employees"}
            </h2>
            {selectedEmployee && (
              <p className="text-sm text-muted-foreground">
                {selectedEmployee.designation}
                {selectedEmployee.department ? ` · ${selectedEmployee.department}` : ""}
              </p>
            )}
          </div>

          <SummaryStrip summary={summary} />

          <div className="flex flex-wrap items-center gap-2">
            <SearchInput
              value={searchText}
              onChange={setSearchText}
              onSearch={() => setFilters({ search: searchText.trim() })}
              showClear={!!filters.search}
              onClear={() => { setSearchText(""); setFilters({ search: "" }); }}
              placeholder="Search notes or employee…"
            />
            <div className="flex items-center gap-1.5">
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ dateFrom: e.target.value })}
                className={cn(FORM_CONTROL, "px-2.5 py-1.5")}
                aria-label="From date"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ dateTo: e.target.value })}
                className={cn(FORM_CONTROL, "px-2.5 py-1.5")}
                aria-label="To date"
              />
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground px-2 py-1 transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Reset
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by conductor">
            <FilterChip active={!filters.conductorRole} onClick={() => setFilters({ conductorRole: "" })}>Any conductor</FilterChip>
            {CONDUCTOR_ROLES.map((r) => (
              <FilterChip key={r} active={filters.conductorRole === r} onClick={() => setFilters({ conductorRole: r })}>{r}</FilterChip>
            ))}
          </div>

          {error && (
            <div role="alert" className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded-xl px-4 py-3">
              {error}
            </div>
          )}

          {meetingsLoading && meetings.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : meetings.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center px-6 py-16 bg-surface border border-dashed border-border rounded-xl text-muted-foreground">
              {hasActiveFilters ? (
                <>
                  <p className="text-sm">No meetings match these filters.</p>
                  <button onClick={clearFilters} className="mt-3 text-primary hover:text-primary/80 text-sm underline">Clear filters</button>
                </>
              ) : (
                <>
                  <CalendarPlus className="w-8 h-8 mb-3" />
                  <p className="text-sm font-medium text-foreground">
                    {selectedEmployee ? `No meetings logged for ${selectedEmployee.name} yet` : "No meetings logged yet"}
                  </p>
                  <p className="mt-1 text-sm max-w-md">
                    Use this page to record a 1:1 where a problem or concern is raised with an employee: what was raised, what came of it, and who held the meeting.
                    Scored reviews belong under Employee Evaluation.
                  </p>
                  {canEdit && (
                    <Button variant="primary" onClick={() => openAdd()} buttonClasses="mt-4">
                      <Plus className="w-4 h-4 mr-1.5 inline" />
                      {selectedEmployee ? `Log a meeting for ${selectedEmployee.name}` : "Log the first meeting"}
                    </Button>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className={cn("space-y-3 transition-opacity", meetingsLoading && "opacity-60")}>
              {meetings.map((m) => (
                <MeetingRow
                  key={m.id}
                  meeting={m}
                  showEmployee={!selectedEmployee}
                  highlight={filters.search}
                  canEdit={canEdit}
                  canDelete={superAdmin}
                  onOpen={setViewingMeeting}
                  onEdit={openEdit}
                  onDelete={(id) => setDeleting(meetings.find((x) => x.id === id) ?? null)}
                />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center pt-1">
              <PagerNav page={page} totalPages={totalPages} onChange={setPage} />
            </div>
          )}
          {totalCount > 0 && (
            <p className="text-xs text-muted-foreground text-center">{totalCount} meeting{totalCount === 1 ? "" : "s"}</p>
          )}
        </div>
      </div>

      {viewingMeeting && (
        <MeetingDetailModal
          meeting={viewingMeeting}
          canEdit={canEdit}
          onEdit={openEdit}
          onClose={() => setViewingMeeting(null)}
          onNewer={viewingIndex > 0 ? () => setViewingMeeting(meetings[viewingIndex - 1]) : undefined}
          onOlder={viewingIndex >= 0 && viewingIndex < meetings.length - 1 ? () => setViewingMeeting(meetings[viewingIndex + 1]) : undefined}
        />
      )}

      {showForm && (
        <MeetingForm
          employees={employees}
          defaultEmployeeId={formEmployeeId}
          initial={editingMeeting}
          onSave={editingMeeting
            ? (data) => updateMeeting(editingMeeting.id, data)
            : (data) => createMeeting(data)
          }
          onClose={() => setShowForm(false)}
        />
      )}

      {deleting && (
        <ConfirmModal
          title="Delete meeting record?"
          message={`This permanently removes ${deleting.employee_name}'s meeting from ${formatMeetingDate(deleting.meeting_date)} (${deleting.conductor_role}). This cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={async () => { await deleteMeeting(deleting.id); setDeleting(null); }}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

export default function MeetingsPage() {
  return (
    <MeetingsProvider>
      <MeetingsContent />
    </MeetingsProvider>
  );
}
